import axios from "axios";

export class RealtimeService {
  private static instance: RealtimeService;
  private isInitialized: boolean = false;
  private timeRef: string | null = null;
  private listeners: Map<string, Set<EventCallback>> = new Map();
  private readonly pollingInterval: number = 5000;
  private intervalId: number | null = null;

  private constructor() {
  }

  public static getInstance(): RealtimeService {
    if (!RealtimeService.instance) {
      RealtimeService.instance = new RealtimeService();
    }
    return RealtimeService.instance;
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized) return;

    await this.fetchNotifications();
    this.startPolling();
    this.isInitialized = true;
  }

  public async on(eventName: EventName, callback: EventCallback): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (Array.isArray(eventName)) {
      eventName.forEach(name => {
        if (!this.listeners.has(name)) {
          this.listeners.set(name, new Set());
        }
        this.listeners.get(name)?.add(callback);
      });
      return;
    }

    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, new Set());
    }
    this.listeners.get(eventName)?.add(callback);
  }

  public off(eventName: EventName, callback: EventCallback): void {
    if (Array.isArray(eventName)) {
      eventName.forEach(name => {
        this.listeners.get(name)?.delete(callback);
        if (this.listeners.get(name)?.size === 0) {
          this.listeners.delete(name);
        }
      });
      return;
    }

    this.listeners.get(eventName)?.delete(callback);
    if (this.listeners.get(eventName)?.size === 0) {
      this.listeners.delete(eventName);
    }
  }

  private async fetchNotifications(): Promise<void> {
    const url = this.timeRef === null
      ? '/ping?timeRef'
      : `/ping?timeRef=${encodeURIComponent(this.timeRef)}`;

    const response = await axios.get<NotificationResponse>(url);

    this.timeRef = response.data.timeRef;
    this.processNotifications(response.data.notifications);
  }

  private processNotifications(notifications: NotificationData[]): void {
    notifications.forEach(notification => {
      const parsedData = typeof notification.data === 'string'
        ? JSON.parse(notification.data)
        : notification.data;

      const listeners = this.listeners.get(notification.event);
      if (listeners) {
        listeners.forEach(callback => callback(notification.event, parsedData));
      }
    });
  }

  private startPolling(): void {
    if (this.intervalId) return;

    this.intervalId = window.setInterval(async () => {
      await this.fetchNotifications();
    }, this.pollingInterval);
  }

  public stopPolling(): void {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  public dispose(): void {
    this.stopPolling();
    this.listeners.clear();
  }
}

export const realtimeService = RealtimeService.getInstance();
